// Local USDA API Service - Queries our Supabase database instead of external USDA API
// This provides the same interface as the external API but uses local data

import { supabase } from './supabase';

export interface LocalUSDAFood {
  id: number;
  fdc_id: number;
  description: string;
  brand_name?: string | null;
  brand_owner?: string | null;
  data_type: 'Foundation' | 'SR Legacy' | 'Survey (FNDDS)' | 'Branded' | 'Experimental';
  serving_size?: number;
  serving_size_unit?: string;
  nutrients: NutrientData[] | { nutrients: NutrientData[] }; // Handle both formats
  ingredients?: string | null;
  category?: string;
  gtin_upc?: string | null;
  data_source_priority: number; // 1=Foundation, 2=Branded, 3=Survey, 4=SR Legacy, 5=Experimental
  created_at: string;
  updated_at: string;
}

export interface NutrientData {
  nutrient_id: number;
  nutrient_name: string;
  amount: number;
  unit: string;
}

export interface USDASearchResponse {
  results: LocalUSDAFood[];
  total: number;
  page: number;
  per_page: number;
}

export interface MacroData {
  calories: number;
  carbs: number;
  protein: number;
  fat: number;
  fiber: number;
}

// Sample fallback data if database isn't populated yet
const sampleFoodsData: LocalUSDAFood[] = [
  {
    id: 1,
    fdc_id: 123456,
    description: 'Apple, raw, with skin',
    brand_name: null,
    brand_owner: null,
    data_type: 'Foundation',
    serving_size: 100,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 0.3, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0.2, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 14, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 52, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 2.4, unit: 'g' }
    ],
    ingredients: null,
    category: 'Fruits and Fruit Juices',
    gtin_upc: null,
    data_source_priority: 1,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 2,
    fdc_id: 234567,
    description: 'Chicken breast, skinless, boneless, raw',
    brand_name: null,
    brand_owner: null,
    data_type: 'Foundation',
    serving_size: 100,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 23.2, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 2.6, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 0, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 120, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 0, unit: 'g' }
    ],
    ingredients: null,
    category: 'Poultry Products',
    gtin_upc: null,
    data_source_priority: 1,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 3,
    fdc_id: 345678,
    description: 'Oatmeal, plain, cooked',
    brand_name: null,
    brand_owner: null,
    data_type: 'Foundation',
    serving_size: 100,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 2.4, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 1.4, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 12, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 68, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 1.7, unit: 'g' }
    ],
    ingredients: null,
    category: 'Cereal Grains and Pasta',
    gtin_upc: null,
    data_source_priority: 1,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 4,
    fdc_id: 456789,
    description: 'Greek yogurt, plain, nonfat',
    brand_name: 'Chobani',
    brand_owner: 'Chobani LLC',
    data_type: 'Branded',
    serving_size: 170,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 17, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 6, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 100, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 0, unit: 'g' }
    ],
    ingredients: 'Cultured pasteurized nonfat milk, live and active cultures',
    category: 'Dairy and Egg Products',
    gtin_upc: '894700001019',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 5,
    fdc_id: 456789,
    description: 'Coca-Cola Classic',
    brand_name: 'Coca-Cola',
    brand_owner: 'The Coca-Cola Company',
    data_type: 'Branded',
    serving_size: 355,
    serving_size_unit: 'ml',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 0, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 39, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 140, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 0, unit: 'g' }
    ],
    ingredients: 'Carbonated water, high fructose corn syrup, caramel color, phosphoric acid, natural flavors, caffeine',
    category: 'Beverages',
    gtin_upc: '049000050103',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 6,
    fdc_id: 678901,
    description: 'Rice, white, long-grain, regular, raw',
    brand_name: null,
    brand_owner: null,
    data_type: 'Foundation',
    serving_size: 100,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 7.1, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 0.7, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 80, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 365, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 1.3, unit: 'g' }
    ],
    ingredients: null,
    category: 'Cereal Grains and Pasta',
    gtin_upc: null,
    data_source_priority: 1,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 7,
    fdc_id: 789013,
    description: 'Kind Bar, Dark Chocolate Nuts & Sea Salt',
    brand_name: 'KIND',
    brand_owner: 'KIND LLC',
    data_type: 'Branded',
    serving_size: 40,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 6, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 16, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 14, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 200, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 7, unit: 'g' }
    ],
    ingredients: 'Almonds, cashews, dark chocolate, sea salt, honey',
    category: 'Snacks',
    gtin_upc: '602652171711',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 8,
    fdc_id: 890124,
    description: 'Milk, reduced fat, 2% milkfat',
    brand_name: null,
    brand_owner: null,
    data_type: 'Foundation',
    serving_size: 100,
    serving_size_unit: 'ml',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 3.4, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 2, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 4.8, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 50, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 0, unit: 'g' }
    ],
    ingredients: null,
    category: 'Dairy and Egg Products',
    gtin_upc: null,
    data_source_priority: 1,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },

  // McDonald's Menu Items
  {
    id: 9,
    fdc_id: 2000001,
    description: 'Big Mac',
    brand_name: 'McDonald\'s',
    brand_owner: 'McDonald\'s Corporation',
    data_type: 'Branded',
    serving_size: 219,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 25, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 29, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 45, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 550, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 3, unit: 'g' }
    ],
    ingredients: 'Beef patty, lettuce, cheese, pickles, onions, special sauce, sesame seed bun',
    category: 'Fast Food',
    gtin_upc: '123456789014',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 10,
    fdc_id: 2000002,
    description: 'McChicken',
    brand_name: 'McDonald\'s',
    brand_owner: 'McDonald\'s Corporation',
    data_type: 'Branded',
    serving_size: 142,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 14, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 16, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 30, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 360, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 2, unit: 'g' }
    ],
    ingredients: 'Chicken patty, lettuce, mayonnaise, bun',
    category: 'Fast Food',
    gtin_upc: '123456789015',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 11,
    fdc_id: 2000003,
    description: 'Quarter Pounder with Cheese',
    brand_name: 'McDonald\'s',
    brand_owner: 'McDonald\'s Corporation',
    data_type: 'Branded',
    serving_size: 195,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 30, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 26, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 42, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 520, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 2.5, unit: 'g' }
    ],
    ingredients: 'Beef patty, cheese, lettuce, tomato, onion, pickle, ketchup, mustard, bun',
    category: 'Fast Food',
    gtin_upc: '123456789016',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 12,
    fdc_id: 2000004,
    description: 'McNuggets (6 piece)',
    brand_name: 'McDonald\'s',
    brand_owner: 'McDonald\'s Corporation',
    data_type: 'Branded',
    serving_size: 85,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 16, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 15, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 15, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 280, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 1, unit: 'g' }
    ],
    ingredients: 'Chicken, water, salt, seasoning, breading',
    category: 'Fast Food',
    gtin_upc: '123456789017',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 13,
    fdc_id: 2000005,
    description: 'French Fries (Medium)',
    brand_name: 'McDonald\'s',
    brand_owner: 'McDonald\'s Corporation',
    data_type: 'Branded',
    serving_size: 117,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 3, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 15, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 44, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 340, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 4, unit: 'g' }
    ],
    ingredients: 'Potatoes, vegetable oil, salt',
    category: 'Fast Food',
    gtin_upc: '123456789018',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 14,
    fdc_id: 2000006,
    description: 'McFlurry Oreo',
    brand_name: 'McDonald\'s',
    brand_owner: 'McDonald\'s Corporation',
    data_type: 'Branded',
    serving_size: 200,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 8, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 18, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 58, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 420, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 1, unit: 'g' }
    ],
    ingredients: 'Soft serve ice cream, Oreo cookie pieces',
    category: 'Fast Food',
    gtin_upc: '123456789019',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },

  // Other Restaurant Chains
  {
    id: 15,
    fdc_id: 3000001,
    description: 'Whopper',
    brand_name: 'Burger King',
    brand_owner: 'Restaurant Brands International',
    data_type: 'Branded',
    serving_size: 270,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 25, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 35, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 49, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 660, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 3, unit: 'g' }
    ],
    ingredients: 'Beef patty, lettuce, tomato, onion, pickle, mayonnaise, ketchup, sesame seed bun',
    category: 'Fast Food',
    gtin_upc: '123456789020',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 16,
    fdc_id: 3000002,
    description: 'Chicken Sandwich',
    brand_name: 'Chick-fil-A',
    brand_owner: 'Chick-fil-A, Inc.',
    data_type: 'Branded',
    serving_size: 185,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 28, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 12, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 41, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 440, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 2, unit: 'g' }
    ],
    ingredients: 'Chicken breast, pickle, buttered bun',
    category: 'Fast Food',
    gtin_upc: '123456789021',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  },
  {
    id: 17,
    fdc_id: 3000003,
    description: 'Crunchwrap Supreme',
    brand_name: 'Taco Bell',
    brand_owner: 'Yum! Brands',
    data_type: 'Branded',
    serving_size: 200,
    serving_size_unit: 'g',
    nutrients: [
      { nutrient_id: 1003, nutrient_name: 'Protein', amount: 18, unit: 'g' },
      { nutrient_id: 1004, nutrient_name: 'Total lipid (fat)', amount: 22, unit: 'g' },
      { nutrient_id: 1005, nutrient_name: 'Carbohydrate, by difference', amount: 35, unit: 'g' },
      { nutrient_id: 1008, nutrient_name: 'Energy', amount: 530, unit: 'kcal' },
      { nutrient_id: 1079, nutrient_name: 'Fiber, total dietary', amount: 3, unit: 'g' }
    ],
    ingredients: 'Tortilla, beef, nacho cheese, sour cream, lettuce, tomatoes',
    category: 'Fast Food',
    gtin_upc: '123456789022',
    data_source_priority: 2,
    created_at: '2024-04-01T12:00:00',
    updated_at: '2024-04-01T12:00:00'
  }
];

class LocalUSDAApiService {
  private readonly baseUrl = 'local'; // Indicates this is a local service
  private readonly maxResults = 50;

  // Search foods in our local database
  async searchFoods(query: string, page = 1, limit = 20): Promise<USDASearchResponse> {
    try {
      const offset = (page - 1) * limit;
      
      // Enhanced search query that prioritizes branded foods for restaurant searches
      const searchQuery = query.toLowerCase();
      const isRestaurantSearch = searchQuery.includes('mcdonald') || 
                                searchQuery.includes('burger king') || 
                                searchQuery.includes('chick-fil-a') || 
                                searchQuery.includes('taco bell') ||
                                searchQuery.includes('wendy') ||
                                searchQuery.includes('subway') ||
                                searchQuery.includes('kfc') ||
                                searchQuery.includes('pizza hut') ||
                                searchQuery.includes('domino') ||
                                searchQuery.includes('starbucks');
      
      // First, search in the comprehensive branded foods table
      let brandedQuery = `
        SELECT 
          fdc_id,
          short_description as description,
          brand_name,
          brand_owner,
          gtin_upc,
          'Branded' as data_type,
          2 as data_source_priority,
          household_serving_size as serving_size,
          household_serving_size_unit as serving_size_unit,
          NULL as ingredients,
          NULL as category
        FROM usda_branded_foods 
        WHERE (
          short_description ILIKE $1 OR 
          brand_name ILIKE $1 OR 
          brand_owner ILIKE $1
        )
      `;
      
      // Add priority boost for restaurant searches
      if (isRestaurantSearch) {
        brandedQuery += `
          ORDER BY 
            CASE 
              WHEN short_description ILIKE $2 THEN 1
              WHEN brand_name ILIKE $2 THEN 2
              ELSE 3
            END,
            fdc_id
        `;
      } else {
        brandedQuery += ` ORDER BY fdc_id`;
      }
      
      brandedQuery += ` LIMIT $3 OFFSET $4`;
      
      const brandedParams = isRestaurantSearch 
        ? [`%${searchQuery}%`, `%${searchQuery}%`, limit, offset]
        : [`%${searchQuery}%`, limit, offset];
      
      // Use direct database query instead of Supabase RPC
      const { data: brandedData, error: brandedError } = await supabase
        .from('usda_branded_foods')
        .select('fdc_id, short_description, brand_name, brand_owner, gtin_upc, household_serving_size, household_serving_size_unit')
        .or(`short_description.ilike.%${searchQuery}%,brand_name.ilike.%${searchQuery}%,brand_owner.ilike.%${searchQuery}%`)
        .limit(limit)
        .range(offset, offset + limit - 1);
      
      if (brandedError) {
        console.error('Error querying branded foods:', brandedError);
      }
      
      // Transform branded data to match our format
      const brandedResults = (brandedData || []).map(row => ({
        fdc_id: row.fdc_id,
        description: row.short_description,
        brand_name: row.brand_name,
        brand_owner: row.brand_owner,
        data_type: 'Branded' as const,
        serving_size: row.household_serving_size,
        serving_size_unit: row.household_serving_size_unit,
        nutrients: [], // Will be populated separately
        ingredients: null,
        category: null,
        gtin_upc: row.gtin_upc,
        data_source_priority: 2
      }));
      
      // If we found enough results in branded foods, return them
      if (brandedResults.length >= limit) {
        return {
          total: brandedResults.length,
          page,
          per_page: limit,
          results: brandedResults
        };
      }
      
      // If not enough branded results, also search in the sample foods table
      const { data: sampleData, error: sampleError } = await supabase
        .from('usda_foods')
        .select('*')
        .or(`description.ilike.%${searchQuery}%,brand_name.ilike.%${searchQuery}%,brand_owner.ilike.%${searchQuery}%`)
        .limit(limit - brandedResults.length)
        .range(0, limit - brandedResults.length - 1);
      
      if (sampleError) {
        console.error('Error querying sample foods:', sampleError);
      }
      
      // Combine results
      const allFoods = [
        ...brandedResults,
        ...(sampleData || [])
      ];
      
      return {
        total: allFoods.length,
        page,
        per_page: limit,
        results: allFoods
      };
      
    } catch (error) {
      console.error('Error searching foods:', error);
      return {
        total: 0,
        page,
        per_page: limit,
        results: []
      };
    }
  }

  // Get food by FDC ID
  async getFoodByFdcId(fdcId: number): Promise<LocalUSDAFood | null> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .eq('fdc_id', fdcId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw new Error(`USDA lookup failed: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('USDA lookup error:', error);
      return null;
    }
  }

  // Get food by internal ID
  async getFoodById(id: number): Promise<LocalUSDAFood | null> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw new Error(`USDA lookup failed: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('USDA lookup error:', error);
      return null;
    }
  }

  // Search by brand
  async searchByBrand(brand: string, limit = 20): Promise<LocalUSDAFood[]> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .or(`brand_name.ilike.%${brand}%,brand_owner.ilike.%${brand}%`)
        .order('description')
        .limit(limit);

      if (error) {
        throw new Error(`Brand search failed: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Brand search error:', error);
      return [];
    }
  }

  // Search by category
  async searchByCategory(category: string, limit = 20): Promise<LocalUSDAFood[]> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .ilike('category', `%${category}%`)
        .order('description')
        .limit(limit);

      if (error) {
        throw new Error(`Category search failed: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Category search error:', error);
      return [];
    }
  }

  // Search by data type (Foundation, Branded, Survey, etc.)
  async searchByDataType(dataType: string, limit = 20): Promise<LocalUSDAFood[]> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .eq('data_type', dataType)
        .order('description')
        .limit(limit);

      if (error) {
        throw new Error(`Data type search failed: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Data type search error:', error);
      return [];
    }
  }

  // Get popular foods (most commonly searched)
  async getPopularFoods(limit = 10): Promise<LocalUSDAFood[]> {
    try {
      // For now, prioritize Foundation and SR Legacy foods as they're most reliable
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .in('data_type', ['Foundation', 'SR Legacy'])
        .not('nutrients', 'is', null)
        .order('description')
        .limit(limit);

      if (error) {
        throw new Error(`Popular foods query failed: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Popular foods error:', error);
      return [];
    }
  }

  // Get foods with highest protein content
  async getHighProteinFoods(limit = 20): Promise<LocalUSDAFood[]> {
    try {
      // This would require a more complex query to sort by protein content
      // For now, return foods that typically have high protein
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .or('description.ilike.%meat%,description.ilike.%chicken%,description.ilike.%fish%,description.ilike.%egg%,description.ilike.%protein%')
        .order('description')
        .limit(limit);

      if (error) {
        throw new Error(`High protein foods query failed: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('High protein foods error:', error);
      return [];
    }
  }

  // Get database statistics
  async getDatabaseStats(): Promise<{
    totalFoods: number;
    totalBrands: number;
    dataTypes: string[];
    lastUpdated: string;
  }> {
    try {
      // Get total foods count
      const { count: totalFoods, error: countError } = await supabase
        .from('usda_foods')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        throw new Error(`Count query failed: ${countError.message}`);
      }

      // Get unique brands count
      const { data: brands, error: brandError } = await supabase
        .from('usda_foods')
        .select('brand_owner')
        .not('brand_owner', 'is', null);

      if (brandError) {
        throw new Error(`Brand query failed: ${brandError.message}`);
      }

      const uniqueBrands = new Set(brands?.map(b => b.brand_owner)).size;

      // Get data types
      const { data: dataTypes, error: typeError } = await supabase
        .from('usda_foods')
        .select('data_type')
        .not('data_type', 'is', null);

      if (typeError) {
        throw new Error(`Data type query failed: ${typeError.message}`);
      }

      const uniqueDataTypes = Array.from(new Set(dataTypes?.map(dt => dt.data_type)));

      // Get last update time from metadata
      const { data: metadata } = await supabase
        .from('data_import_metadata')
        .select('last_import_date')
        .eq('source_name', 'USDA_FDC')
        .single();

      const lastUpdated = metadata?.last_import_date || new Date().toISOString();

      return {
        totalFoods: totalFoods || 0,
        totalBrands: uniqueBrands,
        dataTypes: uniqueDataTypes,
        lastUpdated
      };
    } catch (error) {
      console.error('Database stats error:', error);
      return {
        totalFoods: 0,
        totalBrands: 0,
        dataTypes: [],
        lastUpdated: new Date().toISOString()
      };
    }
  }

  // Extract macros from food nutrients
  extractMacros(food: LocalUSDAFood): MacroData {
    const macros: MacroData = {
      calories: 0,
      carbs: 0,
      protein: 0,
      fat: 0,
      fiber: 0
    };

    // Handle the nutrients structure from the database
    let nutrients: NutrientData[] = [];
    
    try {
      if (Array.isArray(food.nutrients)) {
        // Direct array format (current database structure)
        nutrients = food.nutrients;
      } else if (food.nutrients && typeof food.nutrients === 'object' && Array.isArray(food.nutrients.nutrients)) {
        // Nested format for future compatibility
        nutrients = food.nutrients.nutrients;
      } else if (typeof food.nutrients === 'string') {
        // Handle JSON string format from database
        try {
          const parsed = JSON.parse(food.nutrients);
          if (Array.isArray(parsed)) {
            nutrients = parsed;
          } else if (parsed && Array.isArray(parsed.nutrients)) {
            nutrients = parsed.nutrients;
          } else if (parsed && typeof parsed === 'object') {
            // Handle object format with direct nutrient values
            const nutrientMap = {
              'protein': parsed.protein || parsed.Protein || 0,
              'fat': parsed.fat || parsed.Fat || parsed['Total lipid (fat)'] || 0,
              'carbohydrates': parsed.carbohydrates || parsed.Carbohydrates || parsed['Carbohydrate, by difference'] || 0,
              'calories': parsed.calories || parsed.Calories || parsed.Energy || 0,
              'fiber': parsed.fiber || parsed.Fiber || parsed['Fiber, total dietary'] || 0
            };
            
            macros.protein = Math.round(nutrientMap.protein * 100) / 100;
            macros.fat = Math.round(nutrientMap.fat * 100) / 100;
            macros.carbs = Math.round(nutrientMap.carbohydrates * 100) / 100;
            macros.calories = Math.round(nutrientMap.calories * 100) / 100;
            macros.fiber = Math.round(nutrientMap.fiber * 100) / 100;
            
            console.log('Extracted macros from object format:', macros);
            return macros;
          }
        } catch (parseError) {
          console.warn('Failed to parse nutrients JSON string:', parseError);
          return macros;
        }
      } else {
        console.warn('No nutrients array found for food:', food.description, 'nutrients structure:', food.nutrients);
        return macros;
      }

      console.log('Extracting macros from nutrients array:', nutrients);

      nutrients.forEach(nutrient => {
        if (!nutrient || typeof nutrient !== 'object') {
          console.warn('Invalid nutrient object:', nutrient);
          return;
        }
        
        const name = (nutrient.nutrient_name || '').toLowerCase();
        const amount = nutrient.amount || 0;

        // Energy/Calories (nutrient_id: 1008)
        if (name.includes('energy') || name.includes('calorie') || nutrient.nutrient_id === 1008) {
          macros.calories = Math.round(amount * 100) / 100; // Round to 2 decimal places (preserve USDA precision)
          console.log(`Found calories: ${macros.calories} from "${nutrient.nutrient_name}"`);
        } 
        // Carbohydrates (nutrient_id: 1005)
        else if (name.includes('carbohydrate') || nutrient.nutrient_id === 1005) {
          macros.carbs = Math.round(amount * 100) / 100; // Round to 2 decimal places (preserve USDA precision)
          console.log(`Found carbs: ${macros.carbs} from "${nutrient.nutrient_name}"`);
        } 
        // Protein (nutrient_id: 1003)
        else if (name.includes('protein') || nutrient.nutrient_id === 1003) {
          macros.protein = Math.round(amount * 100) / 100; // Round to 2 decimal places (preserve USDA precision)
          console.log(`Found protein: ${macros.protein} from "${nutrient.nutrient_name}"`);
        } 
        // Fat (nutrient_id: 1004)
        else if (name.includes('total lipid') || name.includes('fat') || nutrient.nutrient_id === 1004) {
          macros.fat = Math.round(amount * 100) / 100; // Round to 2 decimal places (preserve USDA precision)
          console.log(`Found fat: ${macros.fat} from "${nutrient.nutrient_name}"`);
        } 
        // Fiber (nutrient_id: 1079)
        else if (name.includes('fiber') || nutrient.nutrient_id === 1079) {
          macros.fiber = Math.round(amount * 100) / 100; // Round to 2 decimal places (preserve USDA precision)
          console.log(`Found fiber: ${macros.fiber} from "${nutrient.nutrient_name}"`);
        }
      });

      console.log('Final extracted macros (rounded to 2 decimals - preserving USDA precision):', macros);
      return macros;
    } catch (error) {
      console.error('Error extracting macros from food:', food.description, error);
      return macros;
    }
  }

  // Get detailed nutrition for a food
  getNutritionDetails(food: LocalUSDAFood): { [key: string]: { amount: number; unit: string } } {
    const nutrition: { [key: string]: { amount: number; unit: string } } = {};

    // Handle the nested nutrients structure from the database
    let nutrients: NutrientData[] = [];
    
    if (Array.isArray(food.nutrients)) {
      nutrients = food.nutrients;
    } else if (food.nutrients && typeof food.nutrients === 'object' && Array.isArray(food.nutrients.nutrients)) {
      nutrients = food.nutrients.nutrients;
    } else {
      return nutrition;
    }

    nutrients.forEach(nutrient => {
      nutrition[nutrient.nutrient_name] = {
        amount: nutrient.amount || 0,
        unit: nutrient.unit || 'g'
      };
    });

    return nutrition;
  }

  // Check if service is available
  async isServiceAvailable(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('usda_foods')
        .select('id')
        .limit(1);

      return !error;
    } catch (error) {
      console.error('Service availability check failed:', error);
      return false;
    }
  }

  // Get autocomplete suggestions
  async getAutocompleteSuggestions(query: string, limit = 10): Promise<string[]> {
    try {
      if (query.length < 2) return [];

      const { data, error } = await supabase
        .from('usda_foods')
        .select('description, brand_name')
        .or(`description.ilike.%${query}%,brand_name.ilike.%${query}%`)
        .limit(limit);

      if (error) {
        throw new Error(`Autocomplete failed: ${error.message}`);
      }

      const suggestions = new Set<string>();
      
      data?.forEach(food => {
        if (food.description?.toLowerCase().includes(query.toLowerCase())) {
          suggestions.add(food.description);
        }
        if (food.brand_name?.toLowerCase().includes(query.toLowerCase())) {
          suggestions.add(food.brand_name);
        }
      });

      return Array.from(suggestions).slice(0, limit);
    } catch (error) {
      console.error('Autocomplete error:', error);
      return [];
    }
  }

  // Search foods with high specific nutrients
  async searchByNutrient(nutrientName: string, limit = 20): Promise<LocalUSDAFood[]> {
    try {
      // This would require a more complex query to filter by nutrient content
      // For now, return foods that might contain the nutrient based on description
      const searchTerms = [
        nutrientName.toLowerCase(),
        ...this.getNutrientSearchTerms(nutrientName)
      ];

      const searchQuery = searchTerms.map(term => `description.ilike.%${term}%`).join(',');

      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .or(searchQuery)
        .order('description')
        .limit(limit);

      if (error) {
        throw new Error(`Nutrient search failed: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('Nutrient search error:', error);
      return [];
    }
  }

  // Helper function to get search terms for nutrients
  private getNutrientSearchTerms(nutrientName: string): string[] {
    const nutrientMap: { [key: string]: string[] } = {
      'vitamin c': ['citrus', 'orange', 'lemon', 'strawberry', 'kiwi'],
      'calcium': ['milk', 'cheese', 'yogurt', 'dairy'],
      'iron': ['meat', 'spinach', 'beans', 'fortified'],
      'protein': ['meat', 'fish', 'chicken', 'egg', 'bean'],
      'fiber': ['whole grain', 'bean', 'vegetable', 'fruit'],
      'omega-3': ['fish', 'salmon', 'tuna', 'walnut']
    };

    return nutrientMap[nutrientName.toLowerCase()] || [];
  }

  // Get foods by UPC/GTIN code
  async getFoodByUPC(upc: string): Promise<LocalUSDAFood | null> {
    try {
      const { data, error } = await supabase
        .from('usda_foods')
        .select('*')
        .eq('gtin_upc', upc)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        throw new Error(`UPC lookup failed: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('UPC lookup error:', error);
      return null;
    }
  }

  // Get detailed food information including nutrients
  async getFoodDetails(fdcId: number): Promise<LocalUSDAFood | null> {
    try {
      // First try to get from branded foods table
      const { data: brandedFood, error: brandedError } = await supabase
        .from('usda_branded_foods')
        .select('*')
        .eq('fdc_id', fdcId)
        .single();
      
      if (brandedFood && !brandedError) {
        // Get nutrients for this food
        const { data: nutrients, error: nutrientsError } = await supabase
          .from('usda_nutrients')
          .select('*')
          .eq('fdc_id', fdcId);
        
        if (nutrientsError) {
          console.error('Error fetching nutrients:', nutrientsError);
        }
        
        return {
          fdc_id: brandedFood.fdc_id,
          description: brandedFood.short_description,
          brand_name: brandedFood.brand_name,
          brand_owner: brandedFood.brand_owner,
          data_type: 'Branded',
          serving_size: brandedFood.household_serving_size,
          serving_size_unit: brandedFood.household_serving_size_unit,
          nutrients: nutrients || [],
          ingredients: null,
          category: null,
          gtin_upc: brandedFood.gtin_upc,
          data_source_priority: 2
        };
      }
      
      // Fallback to sample foods table
      const { data: sampleFood, error: sampleError } = await supabase
        .from('usda_foods')
        .select('*')
        .eq('fdc_id', fdcId)
        .single();
      
      if (sampleFood && !sampleError) {
        return sampleFood;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting food details:', error);
      return null;
    }
  }
}

// Export singleton instance
export const localUsdaApi = new LocalUSDAApiService();
export default localUsdaApi; 
